import React, { useMemo, useState } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    rectSortingStrategy,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronsUp, ChevronsDown, GripVertical, X } from "lucide-react";

import styles from "./sortablePeople.module.scss";
import { foldForSearch } from "../../helper/searchFold";

/**
 * A drag-to-reorder list of chosen items. Ordering is the actual data — the
 * array order is what the app renders.
 *
 * Dragging alone stops working somewhere around twenty items: a "Top 100"
 * section means dragging a row past eighty others through a scrolling
 * container. So above a threshold the list also offers direct controls —
 * type a position, jump to top or bottom, filter to find one book — and
 * dragging stays for small nudges.
 *
 * `items`: [{ value, label, image }] in display order.
 * `thumbShape`: "circle" (people) or "cover" — a 2:3 book cover cropped into a
 *   circle shows a sliver of its middle and reads as a broken image.
 * `emptyMessage`: overrides the people-specific default.
 * `onChange(nextItems)`: fired on reorder and remove.
 */

// Below this the plain list is easier than a list with a toolbar over it.
const TOOLS_FROM = 8;

function PositionInput({ index, total, onMoveTo, label }) {
    // Local while typing: rewriting the array on every keystroke would move the
    // row out from under the cursor mid-edit.
    const [draft, setDraft] = useState(null);
    const shown = draft ?? String(index + 1);

    const commit = () => {
        if (draft === null) return;
        const wanted = parseInt(draft, 10);
        setDraft(null);
        if (Number.isNaN(wanted)) return;
        const clamped = Math.min(Math.max(wanted, 1), total) - 1;
        if (clamped !== index) onMoveTo(index, clamped);
    };

    return (
        <input
            className={styles.pos}
            type="text"
            inputMode="numeric"
            value={shown}
            aria-label={`Position of ${label}, currently ${index + 1} of ${total}`}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                if (e.key === "Escape") { setDraft(null); e.currentTarget.blur(); }
            }}
        />
    );
}

function Card({ item, index, total, onRemove, onMoveTo, showTools, dragDisabled, imageFit, thumbShape }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.value, disabled: dragDisabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : undefined,
    };

    // The controls sit inside the drag surface, so they have to opt out of the
    // gesture or pressing one starts a drag instead of clicking.
    const notDraggable = { onPointerDown: (e) => e.stopPropagation() };

    return (
        // Pointer drag works anywhere on the card, because confining it to the
        // cover meant grabbing the title did nothing and the card read as stuck.
        // Only the listeners live here though: `attributes` carry role="button"
        // and tabIndex, and a card holding a remove button, a text input and two
        // more buttons must not itself be a button. They go on the grip below,
        // which is also the keyboard path and the visible cue.
        <li
            ref={setNodeRef}
            style={style}
            className={`${styles.card} ${dragDisabled ? "" : styles.cardDraggable}`}
            {...listeners}
        >
            <div className={styles.cardArt}>
                {item.image ? (
                    <img
                        src={item.image}
                        alt=""
                        className={
                            // A face is not a book: cropped to a cover's 2:3 it
                            // loses the sides of the head. People get the round
                            // thumbnail they have everywhere else in the panel,
                            // in the same card as everything else.
                            thumbShape === "circle"
                                ? styles.cardAvatar
                                : imageFit === "contain"
                                    ? `${styles.cardCover} ${styles.cardCoverContain}`
                                    : styles.cardCover
                        }
                    />
                ) : (
                    <span
                        className={thumbShape === "circle" ? styles.cardAvatarFallback : styles.cardCoverFallback}
                        aria-hidden
                    />
                )}
            </div>
            <button
                type="button"
                className={styles.cardRemove}
                onClick={() => onRemove(item.value)}
                aria-label={`Remove ${item.label}`}
                {...notDraggable}
            >
                <X size={13} aria-hidden />
            </button>
            <div className={styles.cardBar} {...notDraggable}>
                {/* The keyboard path, and a visible cue that the card drags. */}
                <button
                    type="button"
                    ref={setActivatorNodeRef}
                    className={styles.cardGrip}
                    disabled={dragDisabled}
                    title={dragDisabled ? "Clear the filter to drag" : "Drag to reorder"}
                    aria-label={`Reorder ${item.label}, currently ${index + 1} of ${total}`}
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical size={13} aria-hidden />
                </button>
                {showTools ? (
                    <PositionInput index={index} total={total} onMoveTo={onMoveTo} label={item.label} />
                ) : (
                    <span className={styles.cardRank}>{index + 1}</span>
                )}
                {showTools && (
                    <>
                        <button
                            type="button"
                            className={styles.move}
                            onClick={() => onMoveTo(index, 0)}
                            disabled={index === 0}
                            aria-label={`Move ${item.label} to the top`}
                            title="Move to top"
                        >
                            <ChevronsUp size={14} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className={styles.move}
                            onClick={() => onMoveTo(index, total - 1)}
                            disabled={index === total - 1}
                            aria-label={`Move ${item.label} to the bottom`}
                            title="Move to bottom"
                        >
                            <ChevronsDown size={14} aria-hidden />
                        </button>
                    </>
                )}
            </div>
            <p className={styles.cardName} title={item.label}>{item.label}</p>
        </li>
    );
}

function Row({ item, index, total, onRemove, onMoveTo, thumbShape, showTools, dragDisabled }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: item.value, disabled: dragDisabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 1 : undefined,
    };

    return (
        <li ref={setNodeRef} style={style} className={styles.row}>
            <button
                type="button"
                className={styles.handle}
                aria-label={`Drag ${item.label} to reorder`}
                disabled={dragDisabled}
                title={dragDisabled ? "Clear the filter to drag" : undefined}
                {...attributes}
                {...listeners}
            >
                <GripVertical size={16} aria-hidden />
            </button>
            {showTools ? (
                <PositionInput index={index} total={total} onMoveTo={onMoveTo} label={item.label} />
            ) : null}
            {item.image ? (
                <img src={item.image} alt="" className={thumbShape === "cover" ? styles.cover : styles.avatar} />
            ) : (
                <span className={thumbShape === "cover" ? styles.coverFallback : styles.avatarFallback} aria-hidden />
            )}
            <span className={styles.name} title={item.label}>{item.label}</span>
            {showTools ? (
                <>
                    <button
                        type="button"
                        className={styles.move}
                        onClick={() => onMoveTo(index, 0)}
                        disabled={index === 0}
                        aria-label={`Move ${item.label} to the top`}
                        title="Move to top"
                    >
                        <ChevronsUp size={15} aria-hidden />
                    </button>
                    <button
                        type="button"
                        className={styles.move}
                        onClick={() => onMoveTo(index, total - 1)}
                        disabled={index === total - 1}
                        aria-label={`Move ${item.label} to the bottom`}
                        title="Move to bottom"
                    >
                        <ChevronsDown size={15} aria-hidden />
                    </button>
                </>
            ) : null}
            <button
                type="button"
                className={styles.remove}
                onClick={() => onRemove(item.value)}
                aria-label={`Remove ${item.label}`}
            >
                <X size={16} aria-hidden />
            </button>
        </li>
    );
}

const SortablePeopleList = ({
    items,
    onChange,
    thumbShape = "circle",
    emptyMessage,
    variant = "list",
    // Book covers are all 2:3, so filling the frame is right for them. Guide
    // artwork is whatever the designer exported, and cropping it to 2:3 cuts
    // the words off the top and bottom — those images are mostly type.
    imageFit = "cover",
    cardMinWidth = 132,
}) => {
    const isGrid = variant === "grid";
    const [filter, setFilter] = useState("");

    const sensors = useSensors(
        // A small drag threshold so a click on the remove button isn't read as a drag.
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const all = useMemo(() => items || [], [items]);
    const showTools = all.length >= TOOLS_FROM;
    const query = foldForSearch(filter.trim());

    // Rows carry their index in the FULL list, so a filtered view still reports
    // and edits true positions rather than positions within the filter.
    const visible = useMemo(
        () =>
            all
                .map((item, index) => ({ item, index }))
                .filter(({ item }) => !query || foldForSearch(item.label).includes(query)),
        [all, query]
    );

    if (all.length === 0) {
        return <p className={styles.empty}>{emptyMessage || "No one selected yet — choose people above."}</p>;
    }

    const ids = all.map((i) => i.value);

    // The grid scrolls with the page rather than inside itself: nesting a tall
    // grid in its own scroll box means two scrollbars and a drag that fights
    // whichever one has the pointer.
    const gridOrListClass = isGrid
        ? styles.grid
        : showTools ? `${styles.list} ${styles.tall}` : styles.list;

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        onChange(arrayMove(all, ids.indexOf(active.id), ids.indexOf(over.id)));
    };

    const handleRemove = (value) => onChange(all.filter((i) => i.value !== value));
    const handleMoveTo = (from, to) => onChange(arrayMove(all, from, to));

    // Dropping a row between two filtered neighbours has no defined meaning in
    // the full list, so dragging is off while a filter is active. The position
    // box and the top/bottom buttons still work, and they are unambiguous.
    const dragDisabled = query.length > 0;

    return (
        <>
            {showTools && (
                <div className={styles.toolbar}>
                    <input
                        className={styles.filter}
                        type="search"
                        value={filter}
                        placeholder="Filter this list…"
                        aria-label="Filter the selected items"
                        onChange={(e) => setFilter(e.target.value)}
                    />
                    <span className={styles.count}>
                        {query ? `${visible.length} of ${all.length}` : `${all.length} selected`}
                    </span>
                </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={ids} strategy={isGrid ? rectSortingStrategy : verticalListSortingStrategy}>
                    <ol
                        className={gridOrListClass}
                        style={
                            isGrid
                                ? { gridTemplateColumns: `repeat(auto-fill, minmax(${cardMinWidth}px, 1fr))` }
                                : undefined
                        }
                    >
                        {visible.map(({ item, index }) =>
                            isGrid ? (
                                <Card
                                    key={item.value}
                                    item={item}
                                    index={index}
                                    total={all.length}
                                    onRemove={handleRemove}
                                    onMoveTo={handleMoveTo}
                                    showTools={showTools}
                                    dragDisabled={dragDisabled}
                                    imageFit={imageFit}
                                    thumbShape={thumbShape}
                                />
                            ) : (
                                <Row
                                    key={item.value}
                                    item={item}
                                    index={index}
                                    total={all.length}
                                    onRemove={handleRemove}
                                    onMoveTo={handleMoveTo}
                                    thumbShape={thumbShape}
                                    showTools={showTools}
                                    dragDisabled={dragDisabled}
                                />
                            )
                        )}
                    </ol>
                </SortableContext>
            </DndContext>
            {showTools && query && (
                <p className={styles.hint}>
                    {visible.length === 0
                        ? "Nothing in this list matches that filter."
                        : "Filtered — drag is off. Use the number box or the top/bottom buttons to move a row."}
                </p>
            )}
        </>
    );
};

export default SortablePeopleList;
